import { type GridColDef } from '@mui/x-data-grid-pro';
import { useDispatch } from 'react-redux';
import type { Asset } from '../../api/models/asset.model';
import type { Contract } from '../../api/models/contract.model';
import type { Invoice } from '../../api/models/invoice.model.ts';
import type { InvoiceAsset } from '../../api/models/invoiceAsset.model.ts';
import type { InvoiceDetails } from '../../api/models/invoiceDetails.model.ts';
import type { NonLeasedAsset } from '../../api/models/nonLeasedAsset.model.ts';
import {
	ACCOUNT_NAME,
	ACTIONS,
	ACTIVATE,
	ADDRESS_1,
	ADDRESS_2,
	ADDRESS_3,
	ADDRESS_4,
	AMOUNT,
	ASSET_NUMBER,
	CATEGORY,
	CITY,
	CONTRACT_NO,
	COUNTRY,
	COUNTY,
	DATE,
	DEACTIVATE,
	DESCRIPTION,
	DUE_DATE,
	EDIT,
	EMAIL,
	END_DATE,
	END_OF_TERM,
	END_OF_TERM_OPTION,
	FIRST_NAME,
	INVOICE_DATE,
	INVOICE_DESCRIPTION,
	INVOICE_DUE_DATE,
	INVOICE_NUMBER,
	LAST_BILLED_RENT,
	LAST_NAME,
	MANUFACTURER,
	MODEL_NO,
	PO_NUMBER,
	POSTAL_CODE,
	ROLE,
	SERIAL_NO,
	START_DATE,
	STATE,
	STATUS,
	SUPPLIER_NAME,
	TAX_AMOUNT,
	TERM,
	TOTAL_FINANCED,
	TOTAL_INVOICE_AMOUNT,
	TOTAL_INVOICE_BALANCE,
} from '../../constants/messages.tsx';
import { setSelectedAssetSerialNo } from '../../redux/slices/assetSlice';
import { setSelectedContractDetailsViewId } from '../../redux/slices/contractSlice';
import { PropToString } from '../../utils/helpers';
import DataGridLinkComponent from './DataGridLinkComponent';
import { setSelectedInvoiceDetailsViewId } from '../../redux/slices/invoiceSlice.ts';
import { Box } from '@mui/material';
import type { Role, User } from '../../api/models/user.model.ts';

// Utility for formatting currency values
const formatCurrency = (value: unknown) => {
	if (value == null || isNaN(Number(value))) return '-';
	return `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

import { useNavigate } from 'react-router-dom';
import { useActivateUserMutation, useDeactivateUserMutation } from '../../api/admin.api.ts';
import type { Audit } from '../../api/models/audit.model.ts';

export const contractProps = PropToString<Contract>();
export const assetProps = PropToString<Asset>();
export const invoiceProps = PropToString<Invoice>();
export const invoiceAssetProps = PropToString<InvoiceAsset>();
export const invoiceDetailsProps = PropToString<InvoiceDetails>();
export const nonLeasedAssetProps = PropToString<NonLeasedAsset>();
const userProps = PropToString<User>();
const auditProps = PropToString<Audit>();

export const contractColumns: GridColDef[] = [
	{
		field: String(contractProps.contractNumber),
		headerName: CONTRACT_NO,
		renderCell: (params) => {
			const dispatch = useDispatch();
			return (
				<DataGridLinkComponent
					onClick={() => {
						dispatch(setSelectedContractDetailsViewId(params.row.contractNumber));
					}}
				>
					{params.row.contractNumber}
				</DataGridLinkComponent>
			);
		},
		flex: 2,
	},
	{ field: String(contractProps.sourceName), headerName: ACCOUNT_NAME, flex: 1 },
	{ field: String(contractProps.contractStatus), headerName: STATUS, flex: 1 },
	{ field: String(contractProps.contractStartDate), headerName: START_DATE, flex: 1 },
	{ field: String(contractProps.contractEndDate), headerName: END_DATE, flex: 1 },
	{ field: String(contractProps.terms), headerName: TERM, flex: 1 },
	{ field: String(contractProps.eotOption), headerName: END_OF_TERM, flex: 1 },
	{
		field: 'lastBilledRent',
		headerName: LAST_BILLED_RENT,
		flex: 1,
		sortable: false,
		valueFormatter: formatCurrency,
	},
];

export const mobileContractColumns: GridColDef[] = [
	{
		field: String(contractProps.contractNumber),
		headerName: CONTRACT_NO,
		flex: 2,
	},
	{ field: String(contractProps.sourceName), headerName: ACCOUNT_NAME, flex: 1 },
	{ field: String(contractProps.contractNumber), headerName: CONTRACT_NO, flex: 2 },
	{ field: String(contractProps.contractStatus), headerName: STATUS, flex: 1 },
	{ field: String(contractProps.contractEndDate), headerName: END_DATE, flex: 1 },
	{ field: String(contractProps.eotOption), headerName: END_OF_TERM_OPTION, flex: 1 },
	{
		field: 'lastBilledRent',
		headerName: LAST_BILLED_RENT,
		flex: 1,
		valueFormatter: formatCurrency,
	},
];

// Detail specifications for layout
export const contractDetailFields = [
	{ field: String(contractProps.contractStatus), headerName: 'Status' },
	{ field: String(contractProps.contractStartDate), headerName: 'Start Date' },
	{ field: String(contractProps.contractEndDate), headerName: 'End Date' },
	{ field: String(contractProps.terms), headerName: 'Term' },
	{ field: String(contractProps.frequency), headerName: 'Original Term Frequency' },
	{ field: String(contractProps.eotOption), headerName: 'End of Term Type' },
	{ field: String(contractProps.currency), headerName: 'Currency' },
	{
		field: 'totalAmountFinanced',
		headerName: 'Total Amount Financed',
		valueFormatter: formatCurrency,
	},
	{ field: String(contractProps.structuredPaymentYn), headerName: 'Structured Payment' },
	{ field: String(contractProps.purchaseOrderNumber), headerName: 'PO Number' },
	{ field: 'lastBilledRent', headerName: 'Last Billed Rent', valueFormatter: formatCurrency },
	{ field: 'lastBilledDate', headerName: 'Last Billed Date' },
];

export const billingInformationFields = [
	{ field: String(contractProps.billingAddress1), headerName: ADDRESS_1 },
	{ field: String(contractProps.billingAddress2), headerName: ADDRESS_2 },
	{ field: String(contractProps.billingAddress3), headerName: ADDRESS_3 },
	{ field: String(contractProps.billingAddress4), headerName: ADDRESS_4 },
	{ field: String(contractProps.billingCity), headerName: CITY },
	{ field: String(contractProps.billingState), headerName: STATE },
	{ field: String(contractProps.billingPostalCode), headerName: POSTAL_CODE },
	{ field: String(contractProps.billingCounty), headerName: COUNTY },
];

export const mobileAssetColumns: GridColDef[] = [
	{ field: String(assetProps.assetNumber), headerName: ASSET_NUMBER, flex: 1 },
	{ field: String(assetProps.assetStatus), headerName: STATUS, flex: 1 },
	{ field: String(assetProps.assetCategory), headerName: CATEGORY, flex: 1 },
	{ field: String(assetProps.make), headerName: MANUFACTURER, flex: 1 },
	{ field: String(assetProps.model), headerName: MODEL_NO, flex: 1 },
];

export const mobileNonLeasedAssetColumns: GridColDef[] = [
	{ field: String(nonLeasedAssetProps.assetNumber), headerName: ASSET_NUMBER, flex: 1 },
	{ field: String(nonLeasedAssetProps.assetCategory), headerName: CATEGORY, flex: 1 },
	{ field: String(nonLeasedAssetProps.manufacturer), headerName: MANUFACTURER, flex: 1 },
	{ field: String(nonLeasedAssetProps.modelNumber), headerName: MODEL_NO, flex: 1 },
];

// NonLeasedAsset column definitions
export const nonLeasedAssetSpecsColumns: GridColDef[] = [
	{
		field: String(nonLeasedAssetProps.assetNumber),
		headerName: ASSET_NUMBER,
		renderCell: (params) => {
			const dispatch = useDispatch();
			return (
				<DataGridLinkComponent
					onClick={() => {
						dispatch(setSelectedAssetSerialNo(params.row.serialNumber));
					}}
				>
					{params.row.assetNumber}
				</DataGridLinkComponent>
			);
		},
		flex: 1,
	},
	{ field: String(nonLeasedAssetProps.sourceName), headerName: ACCOUNT_NAME, flex: 1 },
	{ field: String(nonLeasedAssetProps.assetCategory), headerName: CATEGORY, flex: 1 },
	{ field: String(nonLeasedAssetProps.manufacturer), headerName: MANUFACTURER, flex: 1 },
	{ field: String(nonLeasedAssetProps.modelNumber), headerName: MODEL_NO, flex: 1 },
	{ field: String(nonLeasedAssetProps.serialNumber), headerName: SERIAL_NO, flex: 1 },
	{ field: String(nonLeasedAssetProps.itemDescription), headerName: DESCRIPTION, flex: 2 },
];

export const nonLeasedAssetCustomerAttributesColumns = (
	customerAttributesPerSection: Record<number, Record<string, string>> | undefined,
	customerAttributesSection: number
): GridColDef[] => {
	const columns: GridColDef[] = [
		{
			field: String(nonLeasedAssetProps.assetNumber),
			headerName: ASSET_NUMBER,
			renderCell: (params) => {
				const dispatch = useDispatch();
				return (
					<DataGridLinkComponent
						onClick={() => {
							dispatch(setSelectedAssetSerialNo(params.row.serialNumber));
						}}
					>
						{params.row.assetNumber}
					</DataGridLinkComponent>
				);
			},
			flex: 1,
		},
		{ field: String(nonLeasedAssetProps.assetCategory), headerName: CATEGORY, flex: 1 },
		{ field: String(nonLeasedAssetProps.itemDescription), headerName: DESCRIPTION, flex: 2 },
	];

	if (!customerAttributesPerSection || !customerAttributesPerSection[customerAttributesSection]) return columns;

	const keys = Object.keys(
		customerAttributesPerSection[customerAttributesSection]
	) as Array<string>;

	keys.forEach((headerName) => {
		const udfValue = customerAttributesPerSection[customerAttributesSection][headerName];

		const propKey = headerName as keyof typeof assetProps;
		if (udfValue) {
			columns.push({
				field: String(assetProps[propKey]),
				headerName: String(udfValue),
				flex: 1,
			});
		}
	});

	return columns;
};

// NonLeasedAsset Location columns
export const nonLeasedAssetLocationColumns: GridColDef[] = [
	{
		field: String(nonLeasedAssetProps.assetNumber),
		headerName: ASSET_NUMBER,
		renderCell: (params) => {
			const dispatch = useDispatch();
			return (
				<DataGridLinkComponent
					onClick={() => {
						dispatch(setSelectedAssetSerialNo(params.row.serialNumber));
					}}
				>
					{params.row.assetNumber}
				</DataGridLinkComponent>
			);
		},
		flex: 1,
		minWidth: 100,
	},
	{
		field: String(nonLeasedAssetProps.assetCategory),
		headerName: CATEGORY,
		flex: 1,
		minWidth: 100,
	},
	{
		field: String(nonLeasedAssetProps.itemDescription),
		headerName: DESCRIPTION,
		flex: 2,
		minWidth: 100,
	},
	{
		field: String(nonLeasedAssetProps.eqLocationAddress1),
		headerName: ADDRESS_1,
		flex: 2,
		minWidth: 100,
	},
	{
		field: String(nonLeasedAssetProps.eqLocationAddress2),
		headerName: ADDRESS_2,
		flex: 2,
		minWidth: 100,
	},
	{
		field: String(nonLeasedAssetProps.eqLocationAddress3),
		headerName: ADDRESS_3,
		flex: 2,
		minWidth: 100,
	},
	{
		field: String(nonLeasedAssetProps.eqLocationAddress4),
		headerName: ADDRESS_4,
		flex: 2,
		minWidth: 100,
	},
	{ field: String(nonLeasedAssetProps.eqLocationCity), headerName: CITY, flex: 1, minWidth: 100 },
	{
		field: String(nonLeasedAssetProps.eqLocationZipCode),
		headerName: POSTAL_CODE,
		flex: 1,
		minWidth: 100,
	},
	{
		field: String(nonLeasedAssetProps.eqLocationCounty),
		headerName: COUNTY,
		flex: 1,
		minWidth: 100,
	},
	{ field: String(nonLeasedAssetProps.eqLocationState), headerName: STATE, flex: 1, minWidth: 100 },
	{
		field: String(nonLeasedAssetProps.eqLocationCountry),
		headerName: COUNTRY,
		flex: 1,
		minWidth: 100,
	},
];

// NonLeasedAsset Lease Details columns
export const nonLeasedAssetLeaseDetailsColumns: GridColDef[] = [
	{
		field: String(nonLeasedAssetProps.assetNumber),
		headerName: ASSET_NUMBER,
		renderCell: (params) => {
			const dispatch = useDispatch();
			return (
				<DataGridLinkComponent
					onClick={() => {
						dispatch(setSelectedAssetSerialNo(params.row.serialNumber));
					}}
				>
					{params.row.assetNumber}
				</DataGridLinkComponent>
			);
		},
		flex: 1,
	},
	{ field: String(nonLeasedAssetProps.scheduleNo), headerName: CONTRACT_NO, flex: 2 },
	{
		field: String(nonLeasedAssetProps.purchasePrice),
		headerName: TOTAL_FINANCED,
		flex: 2,
		valueFormatter: (params) =>
			params != null && !isNaN(Number(params))
				? `$${Number(params).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
				: '',
	},
	{ field: String(nonLeasedAssetProps.assetCategory), headerName: CATEGORY, flex: 1 },
	{ field: String(nonLeasedAssetProps.purchaseOrderNo), headerName: PO_NUMBER, flex: 1 },
	{ field: String(nonLeasedAssetProps.supplierName), headerName: SUPPLIER_NAME, flex: 1 },
];

// Asset Specifications columns
export const assetSpecsColumns: GridColDef[] = [
	{
		field: String(assetProps.assetNumber),
		headerName: ASSET_NUMBER,
		renderCell: (params) => {
			const dispatch = useDispatch();
			return (
				<DataGridLinkComponent
					onClick={() => {
						dispatch(setSelectedAssetSerialNo(params.row.serialNumber));
					}}
				>
					{params.row.assetNumber}
				</DataGridLinkComponent>
			);
		},
		flex: 1,
	},
	{ field: String(assetProps.sourceName), headerName: ACCOUNT_NAME, flex: 1 },
	{ field: String(assetProps.assetStatus), headerName: STATUS, flex: 1 },
	{ field: String(assetProps.assetCategory), headerName: CATEGORY, flex: 1 },
	{ field: String(assetProps.make), headerName: MANUFACTURER, flex: 1 },
	{ field: String(assetProps.model), headerName: MODEL_NO, flex: 1 },
	{ field: String(assetProps.serialNumber), headerName: SERIAL_NO, flex: 1 },
	{ field: String(assetProps.itemDescription), headerName: DESCRIPTION, flex: 2 },
];

export const customerAttributesColumns = (
	customerAttributesPerSection: Record<number, Record<string, string>> | undefined,
	customerAttributesSection: number
): GridColDef[] => {
	const columns: GridColDef[] = [
		{
			field: String(assetProps.assetNumber),
			headerName: ASSET_NUMBER,
			renderCell: (params) => {
				const dispatch = useDispatch();
				return (
					<DataGridLinkComponent
						onClick={() => {
							dispatch(setSelectedAssetSerialNo(params.row.serialNumber));
						}}
					>
						{params.row.assetNumber}
					</DataGridLinkComponent>
				);
			},
			flex: 1,
		},
		{ field: String(assetProps.assetStatus), headerName: STATUS, flex: 1 },
		{ field: String(assetProps.assetCategory), headerName: CATEGORY, flex: 1 },
		{ field: String(assetProps.itemDescription), headerName: DESCRIPTION, flex: 2 },
	];

	if (!customerAttributesPerSection || !customerAttributesPerSection[customerAttributesSection]) return columns;

	const keys = Object.keys(
		customerAttributesPerSection[customerAttributesSection]
	) as Array<string>;

	keys.forEach((headerName) => {
		const udfValue = customerAttributesPerSection[customerAttributesSection][headerName];

		const propKey = headerName as keyof typeof assetProps;
		if (udfValue) {
			columns.push({
				field: String(assetProps[propKey]),
				headerName: String(udfValue),
				flex: 1,
			});
		}
	});
	return columns;
};

// Asset Location columns
export const assetLocationColumns: GridColDef[] = [
	{
		field: String(assetProps.assetNumber),
		headerName: ASSET_NUMBER,
		renderCell: (params) => {
			const dispatch = useDispatch();
			return (
				<DataGridLinkComponent
					onClick={() => {
						dispatch(setSelectedAssetSerialNo(params.row.serialNumber));
					}}
				>
					{params.row.assetNumber}
				</DataGridLinkComponent>
			);
		},
		flex: 1,
		minWidth: 100,
	},
	{ field: String(assetProps.assetStatus), headerName: STATUS, flex: 1, minWidth: 100 },
	{ field: String(assetProps.assetCategory), headerName: CATEGORY, flex: 1, minWidth: 100 },
	{ field: String(assetProps.itemDescription), headerName: DESCRIPTION, flex: 2, minWidth: 100 },
	{ field: String(assetProps.installAddress1), headerName: ADDRESS_1, flex: 2, minWidth: 100 },
	{ field: String(assetProps.installAddress2), headerName: ADDRESS_2, flex: 2, minWidth: 100 },
	{ field: String(assetProps.installAddress3), headerName: ADDRESS_3, flex: 2, minWidth: 100 },
	{ field: String(assetProps.installAddress4), headerName: ADDRESS_4, flex: 2, minWidth: 100 },
	{ field: String(assetProps.installCity), headerName: CITY, flex: 1, minWidth: 100 },
	{ field: String(assetProps.installPostalCode), headerName: POSTAL_CODE, flex: 1, minWidth: 100 },
	{ field: String(assetProps.installCounty), headerName: COUNTY, flex: 1, minWidth: 100 },
	{
		field: String(assetProps.installState),
		headerName: STATE,
		flex: 0.5,
		minWidth: 25,
		maxWidth: 50,
	},
	{ field: String(assetProps.installCountry), headerName: COUNTRY, flex: 1, minWidth: 100 },
];

// Asset Lease Details columns
export const leaseDetailsColumns: GridColDef[] = [
	{
		field: String(assetProps.assetNumber),
		headerName: ASSET_NUMBER,
		renderCell: (params) => {
			const dispatch = useDispatch();
			return (
				<DataGridLinkComponent
					onClick={() => {
						dispatch(setSelectedAssetSerialNo(params.row.serialNumber));
					}}
				>
					{params.row.assetNumber}
				</DataGridLinkComponent>
			);
		},
		flex: 1,
	},
	{ field: String(assetProps.assetStatus), headerName: STATUS, flex: 1 },
	{ field: String(assetProps.scheduleNo), headerName: CONTRACT_NO, flex: 2 },
	{ field: String(assetProps.contract_eotOption), headerName: END_OF_TERM_OPTION, flex: 1 },
	{
		field: String(assetProps.purchasePrice),
		headerName: TOTAL_FINANCED,
		flex: 2,
		valueFormatter: (params) =>
			params != null && !isNaN(Number(params))
				? `$${Number(params).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
				: '',
	},
	{ field: String(assetProps.assetCategory), headerName: CATEGORY, flex: 1 },
	{ field: String(assetProps.contract_purchaseOrderNumber), headerName: PO_NUMBER, flex: 1 },
	{ field: String(assetProps.supplierName), headerName: SUPPLIER_NAME, flex: 1 },
	{ field: String(assetProps.origAssetNumber), headerName: 'Original Asset No.', flex: 1 },
	{ field: String(assetProps.splitDate), headerName: 'Split Date', flex: 1 },
];

// InvoiceAsset: Base columns (Asset Number + Asset Quantity - appear in ALL groups)
export const invoiceAssetBaseColumns: GridColDef[] = [
	{
		field: String(invoiceAssetProps.assetNumber),
		headerName: ASSET_NUMBER,
		renderCell: (params) => {
			const dispatch = useDispatch();
			return (
				<DataGridLinkComponent
					onClick={() => {
						dispatch(setSelectedAssetSerialNo(params.row.serialNumber));
					}}
				>
					{params.row.assetNumber}
				</DataGridLinkComponent>
			);
		},
		flex: 1,
	},
	{ field: String(invoiceAssetProps.assetQuantity), headerName: 'Asset Quantity' },
];

// InvoiceAsset: Asset Details columns
export const invoiceAssetAssetDetailsColumns: GridColDef[] = [
	...invoiceAssetBaseColumns,
	{ field: String(invoiceAssetProps.scheduleNo), headerName: CONTRACT_NO, flex: 1 },
	{ field: String(invoiceAssetProps.manufacturer), headerName: MANUFACTURER, flex: 1 },
	{ field: String(invoiceAssetProps.modelNumber), headerName: MODEL_NO, flex: 1 },
	{ field: String(invoiceAssetProps.assetCategory), headerName: CATEGORY, flex: 1 },
	{ field: String(invoiceAssetProps.itemDescription), headerName: DESCRIPTION, flex: 2 },
];

// InvoiceAsset: Invoice Details columns
export const invoiceAssetInvoiceDetailsColumns: GridColDef[] = [
	...invoiceAssetBaseColumns,
	{ field: String(invoiceAssetProps.invoiceDate), headerName: INVOICE_DATE, flex: 1 },
	{ field: String(invoiceAssetProps.invoiceDueDate), headerName: INVOICE_DUE_DATE, flex: 1 },
	{ field: String(invoiceAssetProps.purchaseOrderNo), headerName: PO_NUMBER, flex: 1 },
	{ field: String(invoiceAssetProps.invoiceDescription), headerName: INVOICE_DESCRIPTION, flex: 2 },
	{
		field: String(invoiceAssetProps.invoiceAmount),
		headerName: AMOUNT,
		flex: 1,
		valueFormatter: formatCurrency,
	},
	{
		field: String(invoiceAssetProps.taxAmount),
		headerName: TAX_AMOUNT,
		flex: 1,
		valueFormatter: formatCurrency,
	},
	{
		field: String(invoiceAssetProps.totalInvoiceAmount),
		headerName: TOTAL_INVOICE_AMOUNT,
		flex: 1,
		valueFormatter: formatCurrency,
	},
];

// InvoiceAsset: Asset Location columns
export const invoiceAssetAssetLocationColumns: GridColDef[] = [
	...invoiceAssetBaseColumns,
	{ field: String(invoiceAssetProps.eqLocationAddress1), headerName: ADDRESS_1, flex: 2 },
	{ field: String(invoiceAssetProps.eqLocationAddress2), headerName: ADDRESS_2, flex: 2 },
	{ field: String(invoiceAssetProps.eqLocationAddress3), headerName: ADDRESS_3, flex: 2 },
	{ field: String(invoiceAssetProps.eqLocationCity), headerName: CITY, flex: 1 },
	{ field: String(invoiceAssetProps.eqLocationState), headerName: STATE, flex: 1 },
	{ field: String(invoiceAssetProps.eqLocationZipCode), headerName: POSTAL_CODE, flex: 1 },
	{ field: String(invoiceAssetProps.eqLocationCounty), headerName: COUNTY, flex: 1 },
];

// InvoiceAsset: Billing Location columns
export const invoiceAssetBillingLocationColumns: GridColDef[] = [
	...invoiceAssetBaseColumns,
	{ field: String(invoiceAssetProps.invoiceBillingAddress1), headerName: ADDRESS_1, flex: 2 },
	{ field: String(invoiceAssetProps.invoiceBillingAddress2), headerName: ADDRESS_2, flex: 2 },
	{ field: String(invoiceAssetProps.invoiceBillingAddress3), headerName: ADDRESS_3, flex: 2 },
	{ field: String(invoiceAssetProps.invoiceBillingCity), headerName: CITY, flex: 1 },
	{ field: String(invoiceAssetProps.invoiceBillingState), headerName: STATE, flex: 1 },
	{ field: String(invoiceAssetProps.invoiceBillingPostalCode), headerName: POSTAL_CODE, flex: 1 },
	{ field: String(invoiceAssetProps.invoiceBillingCounty), headerName: COUNTY, flex: 1 },
];

// InvoiceAsset: Customer Attributes columns (UDFs handled dynamically elsewhere)
export const invoiceAssetCustomerAttributesColumns = (
	customerAttributesPerSection: Record<number, Record<string, string>> | undefined,
	customerAttributesSection: number
): GridColDef[] => {
	const columns: GridColDef[] = [
		...invoiceAssetBaseColumns,
		// UDF columns will be dynamically added here
	];
	if (!customerAttributesPerSection || !customerAttributesPerSection[customerAttributesSection]) return columns;
	const keys = Object.keys(
		customerAttributesPerSection[customerAttributesSection]
	) as Array<string>;
	keys.forEach((headerName) => {
		const udfValue = customerAttributesPerSection[customerAttributesSection][headerName];
		const propKey = headerName as keyof typeof invoiceAssetProps;
		if (udfValue && invoiceAssetProps[propKey]) {
			columns.push({
				field: String(invoiceAssetProps[propKey]),
				headerName: String(udfValue),
				flex: 1,
			});
		}
	});
	return columns;
};

// Grouped export for InvoiceAsset columns (must be after all definitions)
export const invoiceAssetColumnGroups = {
	assetDetails: invoiceAssetAssetDetailsColumns,
	invoiceDetails: invoiceAssetInvoiceDetailsColumns,
	assetLocation: invoiceAssetAssetLocationColumns,
	billingLocation: invoiceAssetBillingLocationColumns,
	customerAttributes: invoiceAssetCustomerAttributesColumns,
};

export const auditLogAssetSpecifications: GridColDef[] = [
	{
		field: String(auditProps.batchId),
		headerName: 'Type',
		flex: 1,
		valueGetter: () => {
			return 'UDF';
		},
	},
	{ field: String(auditProps.columnName), headerName: 'Field', flex: 1 },
	{ field: String(auditProps.oldValue), headerName: 'Old Value', flex: 1 },
	{ field: String(auditProps.newValue), headerName: 'New Value', flex: 1 },
	{ field: String(auditProps.updatedBy), headerName: 'Modified by', flex: 1 },
	{
		field: String(auditProps.updatedAt),
		type: 'dateTime',
		headerName: 'Date and Time',
		flex: 1,
		valueGetter: (value) => value && new Date(value),
	},
];

// Combined columns for backwards compatibility - default view
export const assetColumns: GridColDef[] = assetSpecsColumns;

// Invoice Summary columns (desktop)
export const invoiceSummaryColumns: GridColDef[] = [
	{
		field: String(invoiceProps.invoiceNumber),
		headerName: INVOICE_NUMBER,
		renderCell: (params) => {
			const dispatch = useDispatch();
			return (
				<DataGridLinkComponent
					onClick={() => {
						dispatch(setSelectedInvoiceDetailsViewId(params.row.invoiceNumber));
					}}
				>
					{params.row.invoiceNumber}
				</DataGridLinkComponent>
			);
		},
		flex: 2,
		minWidth: 75,
	},
	{ field: String(invoiceProps.invoiceDate), headerName: DATE, flex: 1, minWidth: 75 },
	{ field: String(invoiceProps.invoiceDueDate), headerName: DUE_DATE, flex: 1, minWidth: 75 },
	{ field: String(invoiceProps.contractNumber), headerName: CONTRACT_NO, flex: 1, minWidth: 75 },
	{ field: String(invoiceProps.purchaseOrderNumber), headerName: PO_NUMBER, flex: 1, minWidth: 75 },
	{ field: String(invoiceProps.invoiceLineType), headerName: DESCRIPTION, flex: 2, minWidth: 75 },
	{
		field: String(invoiceProps.invoiceAmount),
		headerName: AMOUNT,
		flex: 1,
		minWidth: 100,
		type: 'number',
		sortable: false,
		valueFormatter: (params) =>
			params != null && !isNaN(Number(params))
				? `$${Number(params).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
				: '',
	},
	{
		field: String(invoiceProps.taxAmount),
		headerName: TAX_AMOUNT,
		flex: 1,
		minWidth: 75,
		type: 'number',
		valueFormatter: (params) =>
			params != null && !isNaN(Number(params))
				? `$${Number(params).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
				: '',
	},
	{
		field: String(invoiceProps.totalInvoiceAmount),
		headerName: TOTAL_INVOICE_AMOUNT,
		flex: 1,
		minWidth: 100,
		type: 'number',
		valueFormatter: (params) =>
			params != null && !isNaN(Number(params))
				? `$${Number(params).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
				: '',
	},
	{
		field: String(invoiceProps.totalInvoiceBalance),
		headerName: TOTAL_INVOICE_BALANCE,
		flex: 1,
		minWidth: 100,
		type: 'number',
		sortable: false,
		valueFormatter: (params) =>
			params != null && !isNaN(Number(params))
				? `$${Number(params).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
				: '',
	},
];

// Billing Info columns (desktop)
export const billingInfoColumns: GridColDef[] = [
	{
		field: String(invoiceProps.invoiceNumber),
		headerName: INVOICE_NUMBER,
		flex: 1,
	},
	{ field: String(invoiceProps.billingAddress1), headerName: ADDRESS_1, flex: 2 },
	{ field: String(invoiceProps.billingAddress2), headerName: ADDRESS_2, flex: 2 },
	{ field: String(invoiceProps.billingAddress3), headerName: ADDRESS_3, flex: 2 },
	{ field: String(invoiceProps.billingAddress4), headerName: ADDRESS_4, flex: 2 },
	{ field: String(invoiceProps.billingCity), headerName: CITY, flex: 1 },
	{ field: String(invoiceProps.billingState), headerName: STATE, flex: 1 },
	{ field: String(invoiceProps.billingPostalCode), headerName: POSTAL_CODE, flex: 1 },
];

// Invoice Summary columns (mobile)
export const mobileInvoiceColumns: GridColDef[] = [
	{
		field: String(invoiceProps.invoiceNumber),
		headerName: INVOICE_NUMBER,
		flex: 1,
	},
	{ field: String(invoiceProps.invoiceDueDate), headerName: DATE, flex: 1 },
	{ field: String(invoiceProps.purchaseOrderNumber), headerName: PO_NUMBER, flex: 1 },
	{ field: String(invoiceProps.contractNumber), headerName: CONTRACT_NO, flex: 1 },
	{
		field: String(invoiceProps.totalInvoiceBalance),
		headerName: TOTAL_INVOICE_BALANCE,
		flex: 1,
		type: 'number',
		valueFormatter: (params) =>
			params != null && !isNaN(Number(params))
				? `$${Number(params).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
				: '',
	},
];

export const invoiceDetailAttributes = [
	{ field: String(invoiceDetailsProps.contractNumbers), headerName: CONTRACT_NO },
	{ field: String(invoiceDetailsProps.invoiceDate), headerName: INVOICE_DATE },
	{ field: String(invoiceDetailsProps.invoiceDueDate), headerName: INVOICE_DUE_DATE },
	{ field: String(invoiceDetailsProps.invoiceLineType), headerName: INVOICE_DESCRIPTION },
	{ field: String(invoiceDetailsProps.purchaseOrderNumbers), headerName: PO_NUMBER },
	{
		field: String(invoiceDetailsProps.invoiceAmount),
		headerName: AMOUNT,
		valueFormatter: formatCurrency,
	},
	{
		field: String(invoiceDetailsProps.taxAmount),
		headerName: TAX_AMOUNT,
		valueFormatter: formatCurrency,
	},
	{
		field: String(invoiceDetailsProps.totalInvoiceAmount),
		headerName: TOTAL_INVOICE_AMOUNT,
		valueFormatter: formatCurrency,
	},
];

const getUniqueRoles = (roles: Role[]) => {
	if (!Array.isArray(roles)) return undefined;
	return Array.from(new Set(roles.map((role) => role.roleName))).join(', ');
};

export const userColumns: GridColDef[] = [
	{ field: String(userProps.firstName), headerName: FIRST_NAME, flex: 1 },
	{ field: String(userProps.lastName), headerName: LAST_NAME, flex: 1 },
	{ field: String(userProps.userStatus), headerName: STATUS, flex: 1 },
	{ field: String(userProps.emailAddress), headerName: EMAIL, flex: 2 },
	{
		field: String(userProps.roles),
		headerName: ROLE,
		flex: 1,
		valueGetter: (roles) => getUniqueRoles(roles),
	},
	{
		field: '',
		headerName: ACTIONS,
		renderCell: (_params) => {
			const navigate = useNavigate();
			const [deactivateUser, { isLoading: isDeactivating }] = useDeactivateUserMutation();
			const [activateUser, { isLoading: isActivating }] = useActivateUserMutation();
			const userId: number | undefined = _params.row.userId;
			const isInactive = _params.row.userStatus === 'INACTIVE';
			return (
				<Box>
					<DataGridLinkComponent
						onClick={() => {
							navigate('/user-management/edit-user', { state: { userId } });
						}}
					>
						{EDIT}
					</DataGridLinkComponent>
					<div style={{ marginLeft: '20px', display: 'inline-block' }}>
						<DataGridLinkComponent
							onClick={async () => {
								if (isDeactivating || isActivating || userId == null) return;
								try {
									if (isInactive) {
										await activateUser({ userId }).unwrap();
									} else {
										await deactivateUser({ userId }).unwrap();
									}
									// MUI DataGrid Pro uses a server-side dataSource that
									// won't react to RTK Query tag invalidation — force the
									// grid to drop its cached page and refetch so the row
									// shows the new status + button label.
									_params.api.dataSource.cache.clear();
									_params.api.dataSource.fetchRows();
								} catch {
									// Mutation error already handled upstream.
								}
							}}
						>
							{isInactive ? ACTIVATE : DEACTIVATE}
						</DataGridLinkComponent>
					</div>
				</Box>
			);
		},
		flex: 2,
	},
];
